import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { ClearInputTextButton } from '@/components/form/baseInputs/Button/ClearInputTextButton';
import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { useOptionalDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { COLORS } from '@/styles/tokens/colors';

import { DocumentSearchResult } from './DocumentSearch.types';
import { formatReferenceCountMessage } from './DocumentSearchBar.utils';
import { DocumentSearchResponse } from './DocumentSearchResponse';

export interface DocumentSearchBarUIProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onSearch: () => Promise<void>;
  onClearSearch: () => void;
  loadingSearch: boolean;
  searchLoading: boolean;
  searchError: boolean;
  searchResponse: string;
  paragraphIds: string[];
  annotations: DocumentSearchResult[] | undefined;
  activeAnnotationIndex: number;
  setActiveAnnotationIndex: (index: number) => void;
  showCopyCitationsButton: boolean;
  positioningYOffset: number | null;
  onDismiss?: () => void;
}

function TextInputEndAdornment({
  searchQuery,
  onClearSearch,
}: Pick<DocumentSearchBarUIProps, 'searchQuery' | 'onClearSearch'>) {
  if (searchQuery.length === 0) {
    return (
      <InfoTooltip
        title={
          <Stack spacing={1} p={0.5}>
            <Typography variant="title-xs">What can I ask?</Typography>
            <Typography variant="body-xs">
              You can search for any information found in the document such as
              details on dispositions & trustees
            </Typography>
          </Stack>
        }
        placement="top-end"
        sx={{ color: COLORS.TEXT.SECONDARY }}
      />
    );
  }
  return (
    <ClearInputTextButton
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClearSearch();
      }}
    />
  );
}

export function DocumentSearchBarUI({
  searchQuery,
  setSearchQuery,
  onSearch,
  onClearSearch,
  loadingSearch,
  searchLoading,
  searchError,
  searchResponse,
  paragraphIds,
  annotations,
  activeAnnotationIndex,
  setActiveAnnotationIndex,
  showCopyCitationsButton,
  positioningYOffset,
  onDismiss,
}: DocumentSearchBarUIProps) {
  const chatContext = useOptionalDocumentChatContext();
  const isAIEnabled = useAICapabilitiesEnabled();
  const isDocumentChatEnabled = !!chatContext && isAIEnabled;

  return (
    <Box
      sx={(t) => ({
        transform: positioningYOffset
          ? `translateY(${positioningYOffset}px)`
          : 'none',
        transition: 'transform 0.1s linear',
        boxShadow: t.palette.shadows.lg,
        background: COLORS.GRAY[50],
        border: `solid 1px ${COLORS.GRAY[200]}`,
        borderRadius: 1,
        p: 2,
        mx: 1,
        mb: 1,
        display: 'flex',
      })}
    >
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Stack direction="column" width="100%" display="flex">
          <Stack
            direction="row"
            display="flex"
            gap={1}
            alignItems="flex-end"
            width="100%"
            component="form"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await onSearch();
            }}
          >
            <Stack sx={{ flexGrow: 1 }}>
              {isDocumentChatEnabled ? (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Stack gap={0.5}>
                    <Typography variant="title-s">References</Typography>
                    <Typography variant="body-s">
                      {formatReferenceCountMessage(annotations, searchQuery)}
                    </Typography>
                  </Stack>
                  <CloseButton
                    size="sm"
                    variant="transparent"
                    onClick={onDismiss}
                  />
                </Stack>
              ) : (
                <TextInput
                  testId="DocumentSearchBar-searchInput"
                  label="Search document"
                  placeholder="Ask something about this document"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startAdornment={
                    <SearchSmIcon
                      size={16}
                      sx={{ color: COLORS.TEXT.SECONDARY }}
                    />
                  }
                  endAdornment={
                    <TextInputEndAdornment
                      searchQuery={searchQuery}
                      onClearSearch={onClearSearch}
                    />
                  }
                />
              )}
            </Stack>
            {!isDocumentChatEnabled && (
              <Button
                type="submit"
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void onSearch();
                }}
                disabled={!searchQuery.length}
                loading={loadingSearch || searchLoading}
              >
                Search
              </Button>
            )}
          </Stack>
          {(searchResponse || searchError || annotations !== undefined) && (
            <Box mt={1}>
              <DocumentSearchResponse
                searchResponse={searchResponse}
                paragraphIds={paragraphIds}
                showCopyCitationsButton={showCopyCitationsButton}
                searchError={searchError}
                annotations={annotations}
                activeAnnotationIndex={activeAnnotationIndex}
                setActiveAnnotationIndex={setActiveAnnotationIndex}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
